import React, { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';

/**
 * PropertyPicker — the anchored popup behind "+ Add filter".
 *
 * Lists the trace properties an eval can filter on, grouped the way the
 * schema groups them (This run · Its run · This span · Meta …), with search
 * over path and label. The same popup the current evals use for their
 * filters, duplicated here rather than imported: features/evals3 never
 * reaches into features/evals, so the old one can be deleted as a file.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   anchorRef   ref to the element the popup anchors below
 *   groups      [{ id, label, fields: [{ path, type, label }] }]
 *   onPick      (field) => void
 */
export default function PropertyPicker({ open, onClose, anchorRef, groups = [], onPick, width = 360 }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      const t = anchorRef?.current?.getBoundingClientRect?.();
      if (!t) return;
      setPos({ top: t.bottom + 6, left: t.left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (!anchorRef?.current?.contains(e.target)) onClose?.(); };
    // Handled here: the dialog or panel around it skips a defaultPrevented Escape.
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose?.(); } };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  // A fresh search every time it opens.
  useEffect(() => { if (open) setQuery(''); }, [open]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const shown = groups
    .map((g) => ({ ...g, fields: q ? g.fields.filter((f) => f.path.toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q)) : g.fields }))
    .filter((g) => g.fields.length);

  return (
    <div
      className="fixed z-[1400] overflow-hidden rounded-[10px] border border-[color:var(--border-default)] bg-[color:var(--surface-raised)]"
      style={{ top: pos.top, left: pos.left, width, fontFamily: 'Inter, sans-serif', boxShadow: 'var(--shadow-lg)' }}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-label="Filter"
    >
      <div className="flex items-center gap-2 border-b border-[color:var(--border-default)] px-2 py-1.5">
        <Search size={14} className="text-[color:var(--text-muted)]" />
        <input
          autoFocus
          type="text"
          placeholder="Search properties…"
          aria-label="Search properties"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
        />
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-0.5 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"><X size={14} /></button>
      </div>
      <div className="max-h-[320px] overflow-auto py-1">
        {shown.length === 0 && <div className="px-3 py-4 text-center text-[12px] text-[color:var(--text-muted)]">No matching properties</div>}
        {shown.map((g) => (
          <div key={g.id} className="py-1">
            <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-disabled)]">{g.label}</div>
            {g.fields.map((f) => (
              <button
                key={f.path}
                type="button"
                onClick={() => onPick?.(f)}
                className="flex h-8 w-full items-center justify-between gap-2 px-3 text-left transition-colors hover:bg-surface-hover"
                title={f.label || undefined}
              >
                {/* A block ("Spans where …") is named by what it opens, not by the path `spans`. */}
                <span className={`truncate text-[12px] text-[color:var(--text-primary)] ${f.type === 'list' ? '' : 'font-mono'}`}>{f.type === 'list' ? f.label : f.path}</span>
                <span className="shrink-0 text-[10px] text-[color:var(--text-muted)]">{f.type}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
